/**
 * Documents-tab notice for the ALPR preservation demand and the Article 39.14 request.
 *
 * Hidden when this case does not use those letters. Shown only when it does, and a required merge
 * field is still blank. Approve and the rest of the Documents tab are unchanged.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, AlertTitle, Typography } from '@mui/material';
import AlprDocumentsApi, {
  AlprDocumentsReadinessDto,
  FieldCoverageReport,
} from '../../api/AlprDocumentsApi';

interface AlprReadinessBannerProps {
  Ticket_guid: string;
}

function missingLabels(report: FieldCoverageReport): string[] {
  return report.missingRequired.map((field) => field.label);
}

const AlprReadinessBanner: React.FC<AlprReadinessBannerProps> = ({ Ticket_guid }) => {
  const [readiness, setReadiness] = useState<AlprDocumentsReadinessDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setReadiness(await AlprDocumentsApi.readiness(Ticket_guid));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check ALPR document readiness');
    }
  }, [Ticket_guid]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }

  if (!readiness || !readiness.applies) {
    return null;
  }

  const preservationMissing = missingLabels(readiness.preservationDemand);
  const articleMissing = missingLabels(readiness.article3914Request);
  if (preservationMissing.length === 0 && articleMissing.length === 0) {
    return null;
  }

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <AlertTitle>ALPR letters are missing fields on this case</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        The preservation demand and the Article 39.14 request print a blank for anything not recorded
        here. Fill these before those letters go out.
      </Typography>
      {preservationMissing.length > 0 && (
        <Typography variant="body2">
          Preservation demand: {preservationMissing.join(', ')}
        </Typography>
      )}
      {articleMissing.length > 0 && (
        <Typography variant="body2">
          Article 39.14 request: {articleMissing.join(', ')}
        </Typography>
      )}
    </Alert>
  );
};

export default AlprReadinessBanner;
